import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Quote } from 'lucide-react'

export interface Testimonial {
  name: string
  content: string
  rating: number
}

interface TestimonialCardProps {
  testimonial: Testimonial
  variant?: 'light' | 'dark'
  previewLength?: number
}

const TestimonialModal: React.FC<{
  testimonial: Testimonial
  onClose: () => void
}> = ({ testimonial, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEsc)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleEsc)
    }
  }, [onClose])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3, type: 'spring', damping: 25 }}
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-end">
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-6">
          <Quote size={32} className="text-gray-200 mb-4" />
          <p className="text-gray-700 leading-relaxed whitespace-pre-line text-base">
            {testimonial.content}
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <div className="font-semibold text-gray-900 text-lg">
            {testimonial.name}
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({
  testimonial,
  variant = 'light',
  previewLength = 180
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const needsTruncation = testimonial.content.length > previewLength

  const previewText = needsTruncation
    ? testimonial.content.slice(0, previewLength).replace(/\s+\S*$/, '') + '...'
    : testimonial.content

  const isLight = variant === 'light'

  return (
    <>
      <div className={`relative h-full flex flex-col justify-between rounded-2xl p-7 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 ${
        isLight
          ? 'bg-white shadow-lg hover:shadow-2xl border border-gray-100'
          : 'bg-white/10 backdrop-blur-md border border-white/10 hover:bg-white/15'
      }`}>
        {/* Decorative quote mark */}
        <Quote
          size={44}
          className={`absolute top-5 right-6 ${isLight ? 'text-brand/10' : 'text-white/10'}`}
          fill="currentColor"
        />
        <div>
          <p className={`leading-relaxed ${isLight ? 'text-gray-700' : 'text-gray-200'}`}>
            "{previewText}"
          </p>
          {needsTruncation && (
            <button
              onClick={() => setIsOpen(true)}
              className={`mt-3 text-sm font-semibold transition-colors ${
                isLight
                  ? 'text-brand hover:text-brand-dark'
                  : 'text-brand-light hover:text-white'
              }`}
            >
              Read Full Story →
            </button>
          )}
        </div>
        <div className="mt-6 pt-5 border-t border-dashed flex items-center gap-3 border-gray-200/60">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand to-brand-light flex items-center justify-center text-white font-bold text-lg shadow-md shadow-brand/20 flex-shrink-0">
            {testimonial.name.charAt(0)}
          </div>
          <div>
            <div className={`font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>
              {testimonial.name}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <TestimonialModal
            testimonial={testimonial}
            onClose={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  )
}

export default TestimonialCard
